import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme, Skeleton } from '@mui/material';
import { Star as StarIcon, Person as PersonIcon } from '@mui/icons-material';
import { User } from 'firebase/auth';
import { movieService, unifiedGetDoc, type CrewPostLinks } from '@watchlist/shared';
import { font } from '../../utils/fontHelper';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { useUserFollow } from '../../contexts/UserFollowContext';
import tmdbRequestQueue from '../../services/tmdbRequestQueue';
import ImageShimmer from './ImageShimmer';
import InitialsAvatar from './InitialsAvatar';
import WatchlistWidget from './WatchlistWidget';
import CompactMovieCard from './CompactMovieCard';
import UserListingCard from './UserListingCard';
import { stashPersonDetailNavPreviewFromPerson } from '../../utils/personDetailNavPreview';
import { parseFirebaseDate } from '../../utils/dateUtils';
import { relativeTime } from '../../utils/crewFeedUtils';
import { reviewToSocialPost, type SocialPostData } from '../../utils/socialPostBuilders';

const TMDB_IMG = (path: string | undefined | null, size = 'w200'): string | undefined => {
  if (!path || path === 'N/A') return undefined;
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://image.tmdb.org/t/p/${size}${normalized}`;
};

const CrewWatchlistRow: React.FC<{
  watchlistId: string;
  actorId: string;
  currentUser?: User | null;
  onSelect?: (id: string) => void;
}> = ({ watchlistId, actorId, currentUser, onSelect }) => {
  const [liveData, setLiveData] = useState<any>(null);

  useEffect(() => {
    unifiedGetDoc('watchlists', watchlistId)
      .then(doc => {
        if (doc?.exists?.()) setLiveData(doc.data());
      })
      .catch(() => {});
  }, [watchlistId]);

  const moviePosters: string[] = liveData?.movieDetails
    ? liveData.movieDetails
        .slice(0, 2)
        .map((m: any) => m.poster || m.Poster || m.poster_path)
        .filter(Boolean)
    : [];

  const externalData = liveData
    ? {
        name: liveData.name || 'Watchlist',
        description: liveData.description,
        isPrivate: false,
        createdBy: actorId,
        createdByName: '',
        movieCount: liveData.movieCount ?? liveData.movieDetails?.length ?? 0,
        movieDetails: liveData.movieDetails || [],
        followerCount: liveData.followerCount || 0,
        collaborators: liveData.collaborators || [],
        collaboratorProfiles: liveData.collaboratorProfiles || [],
      }
    : {
        name: 'Watchlist',
        description: undefined,
        isPrivate: false,
        createdBy: actorId,
        createdByName: '',
      };

  return (
    <WatchlistWidget
      watchlistType={watchlistId}
      user={currentUser}
      targetUserId={actorId}
      isOtherUserWatchlist
      moviePosters={moviePosters}
      onClick={() => onSelect?.(watchlistId)}
      externalWatchlistData={externalData}
    />
  );
};

const CrewMovieLinkRow: React.FC<{ movieId: string; onSelect?: (id: string) => void }> = ({
  movieId,
  onSelect,
}) => {
  const [title, setTitle] = useState<string>('Film');
  const [poster, setPoster] = useState<string | undefined>();
  const [year, setYear] = useState<string | undefined>();
  const [genre, setGenre] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    tmdbRequestQueue
      .getMovieData(movieId)
      .then(m => {
        if (cancelled || !m) return;
        setTitle(m.title || m.Title || 'Film');
        const p = m.poster_path || m.Poster;
        setPoster(p || undefined);
        setYear(m.release_date?.slice(0, 4) || m.Year || undefined);
        setGenre(m.genres?.[0]?.name || undefined);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [movieId]);

  return (
    <Box sx={{ mb: 1, maxWidth: { xs: '100%', sm: '70%', md: '45%' } }}>
      <CompactMovieCard
        poster={poster}
        title={title}
        year={year}
        genre={genre}
        onClick={onSelect ? () => onSelect(movieId) : undefined}
      />
    </Box>
  );
};

const CrewReviewLinkRow: React.FC<{
  reviewId: string;
  onSelect?: (id: string) => void;
  onOpenDialog?: (post: SocialPostData) => void;
}> = ({ reviewId, onSelect, onOpenDialog }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const skBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState<any>(null);
  const [movieTitle, setMovieTitle] = useState<string>('Film');
  const [posterSrc, setPosterSrc] = useState<string | undefined>();
  const [rawPosterPath, setRawPosterPath] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await movieService.getShareableReview(reviewId);
        if (cancelled || !r) { setLoading(false); return; }
        setReviewData(r);
        const movieId: string = r.movieId || reviewId.split('_').slice(1).join('_');
        if (movieId) {
          try {
            const m = await tmdbRequestQueue.getMovieData(movieId);
            if (!cancelled && m) {
              setMovieTitle(m.title || m.Title || 'Film');
              const p = m.poster_path || m.Poster;
              setRawPosterPath(p || undefined);
              if (p) setPosterSrc(TMDB_IMG(p, 'w200'));
            }
          } catch { /* poster stays empty */ }
        }
      } catch { /* review fetch failed */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reviewId]);

  const handleClick = () => {
    if (onOpenDialog && reviewData) {
      const post = reviewToSocialPost({
        ...reviewData,
        movieTitle,
        moviePoster: rawPosterPath,
      });
      onOpenDialog(post);
    } else {
      onSelect?.(reviewId);
    }
  };

  const cardSx = {
    p: 1.25,
    borderRadius: 2,
    bgcolor: isDark ? '#2C2C2E' : '#F0EFF0',
    mb: 1,
    maxWidth: { xs: '100%', sm: '70%', md: '45%' },
  };

  // ── Skeleton while data loads ───────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', gap: 1.25 }}>
          <Skeleton variant="rounded" width={72} sx={{ borderRadius: 1.5, flexShrink: 0, minHeight: 110, bgcolor: skBg }} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75, pt: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Skeleton variant="circular" width={18} height={18} sx={{ bgcolor: skBg, flexShrink: 0 }} />
              <Skeleton variant="text" width="55%" height={16} sx={{ bgcolor: skBg }} />
            </Box>
            <Skeleton variant="text" width="75%" height={14} sx={{ bgcolor: skBg }} />
            <Skeleton variant="text" width="30%" height={12} sx={{ bgcolor: skBg }} />
            <Skeleton variant="text" width="100%" height={12} sx={{ bgcolor: skBg, mt: 0.25 }} />
            <Skeleton variant="text" width="85%" height={12} sx={{ bgcolor: skBg }} />
          </Box>
        </Box>
      </Box>
    );
  }

  const rating: number | undefined = reviewData?.rating;
  const reviewText: string | undefined = reviewData?.review;
  const userAvatar: string | undefined = reviewData?.userAvatar;
  const userName: string | undefined = reviewData?.userName;
  // Prefer review's last-update time; watchedDate is when they saw the movie (often much older)
  const dateRaw = reviewData?.updatedAt || reviewData?.updatedDate || reviewData?.date;
  const dateObj = dateRaw ? parseFirebaseDate(dateRaw) : null;
  const timeLabel = dateObj ? relativeTime(dateObj.getTime()) : '';
  console.log(`[CrewReviewLinkRow] date fields for ${reviewId}:`, {
    updatedAt: reviewData?.updatedAt,
    updatedDate: reviewData?.updatedDate,
    watchedDate: reviewData?.watchedDate,
    date: reviewData?.date,
    dateRaw,
    resolved: timeLabel,
  });

  return (
    <Box
      onClick={handleClick}
      sx={{
        ...cardSx,
        cursor: 'pointer',
        '&:hover': { bgcolor: isDark ? '#3A3A3C' : '#E5E5EA' },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch' }}>
        {/* Movie poster */}
        <Box sx={{
          width: 72,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: isDark ? '#3A3A3C' : '#E0E0E0',
          alignSelf: 'stretch',
        }}>
          <ImageShimmer src={posterSrc} alt="" width="100%" height="100%" objectFit="cover" borderRadius={1.5} />
        </Box>

        {/* Right info column */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 0.4 }}>
          {/* Line 1: user avatar + name + rating */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <InitialsAvatar src={userAvatar} name={userName || 'User'} size={18} />
            <Typography sx={{
              fontFamily: font(), fontSize: 13, fontWeight: 600,
              color: theme.palette.text.primary, flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {userName || 'User'}
            </Typography>
            {rating != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
                <StarIcon sx={{ fontSize: 12, color: '#FF9500' }} />
                <Typography sx={{ fontFamily: font(), fontSize: 12, fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }}>
                  {rating}/5
                </Typography>
              </Box>
            )}
          </Box>

          {/* Line 2: "Review on Movie Name" */}
          <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {'Review on '}
            <Box component="span" sx={{ fontStyle: 'italic', color: theme.palette.text.primary, fontWeight: 500 }}>
              {movieTitle}
            </Box>
          </Typography>

          {/* Line 3: relative time */}
          {timeLabel && (
            <Typography sx={{ fontFamily: font(), fontSize: 11, color: theme.palette.text.disabled, lineHeight: 1.3 }}>
              {timeLabel}
            </Typography>
          )}

          {/* Review snippet */}
          {reviewText && (
            <Typography sx={{
              fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary,
              lineHeight: 1.45, fontStyle: 'italic',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', mt: 0.25,
            }}>
              "{reviewText}"
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CrewPersonLinkRow: React.FC<{ personId: string; onSelect?: (id: string) => void }> = ({
  personId,
  onSelect,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [name, setName] = useState<string>('Name');
  const [photo, setPhoto] = useState<string | undefined>();
  const [profilePath, setProfilePath] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const n = parseInt(personId, 10);
    if (Number.isNaN(n)) return;
    tmdbService
      .getPersonById(n)
      .then(p => {
        if (cancelled || !p) return;
        setName(p.name || 'Person');
        setProfilePath(p.profile_path ?? null);
        setPhoto(p.profile_path ? TMDB_IMG(p.profile_path, 'w185') : undefined);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [personId]);

  return (
    <Box
      onClick={() => {
        stashPersonDetailNavPreviewFromPerson(
          personId,
          {
            id: personId,
            name,
            profile_path: profilePath,
          },
          () => {
            const w500 = profilePath ? TMDB_IMG(profilePath, 'w500') : undefined;
            return w500 ?? photo ?? null;
          }
        );
        onSelect?.(personId);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        cursor: onSelect ? 'pointer' : 'default',
        bgcolor: isDark ? '#2C2C2E' : '#F0EFF0',
        mb: 1,
        maxWidth: { xs: '100%', sm: '70%', md: '45%' },
        '&:hover': onSelect ? { bgcolor: isDark ? '#3A3A3C' : '#E5E5EA' } : {},
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          overflow: 'hidden',
          flexShrink: 0,
          bgcolor: isDark ? '#3A3A3C' : '#E0E0E0',
        }}
      >
        {photo ? (
          <ImageShimmer src={photo} alt="" width="100%" height="100%" objectFit="cover" borderRadius="50%" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <PersonIcon sx={{ color: theme.palette.text.disabled }} />
          </Box>
        )}
      </Box>
      <Typography sx={{ fontFamily: font(), fontSize: 15, fontWeight: 500, flex: 1, minWidth: 0 }}>
        {name}
      </Typography>
    </Box>
  );
};

export interface CrewPostLinkSectionsProps {
  links: CrewPostLinks;
  crewActorId: string;
  currentUser?: User | null;
  onWatchlistSelect?: (watchlistId: string) => void;
  onMovieSelect?: (movieId: string) => void;
  onReviewSelect?: (reviewId: string) => void;
  onOpenReviewDialog?: (post: SocialPostData) => void;
  onUserSelect?: (userId: string) => void;
  onPersonSelect?: (personId: string) => void;
}

/**
 * Renders deep-link targets for an official Crew post (watchlists, films, reviews, people, users).
 */
const CrewPostLinkSections: React.FC<CrewPostLinkSectionsProps> = ({
  links,
  crewActorId,
  currentUser,
  onWatchlistSelect,
  onMovieSelect,
  onReviewSelect,
  onOpenReviewDialog,
  onUserSelect,
  onPersonSelect,
}) => {
  const { getUserDisplayName, getUserAvatar, getUserProfile } = useUserProfileCache();
  const { followStats, followUser, unfollowUser } = useUserFollow();
  const theme = useTheme();

  const hasAny =
    (links.watchlistIds?.length ?? 0) > 0 ||
    (links.movieIds?.length ?? 0) > 0 ||
    (links.reviewIds?.length ?? 0) > 0 ||
    (links.userIds?.length ?? 0) > 0 ||
    (links.personIds?.length ?? 0) > 0;

  if (!hasAny) return null;

  return (
    <Box>
      {links.watchlistIds && links.watchlistIds.map(id => (
        <Box key={id} sx={{ mb: 1.5 }}>
          <CrewWatchlistRow
            watchlistId={id}
            actorId={crewActorId}
            currentUser={currentUser}
            onSelect={onWatchlistSelect}
          />
        </Box>
      ))}

      {links.movieIds && links.movieIds.map(id => (
        <CrewMovieLinkRow key={id} movieId={id} onSelect={onMovieSelect} />
      ))}

      {links.reviewIds && links.reviewIds.map(id => (
        <CrewReviewLinkRow key={id} reviewId={id} onSelect={onReviewSelect} onOpenDialog={onOpenReviewDialog} />
      ))}

      {links.userIds && links.userIds.length > 0 && (
        <Box sx={{ mb: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
          {links.userIds.map(uid => {
            const profile = getUserProfile(uid);
            return (
              <UserListingCard
                key={uid}
                id={uid}
                name={getUserDisplayName(uid, 'User')}
                username={profile?.username || ''}
                avatar={getUserAvatar(uid)}
                stats={{ followers: profile?.stats?.followersCount ?? 0, watchlists: profile?.stats?.listsCreated ?? 0 }}
                isFollowing={followStats.followedUsers.includes(uid)}
                isCurrentUser={currentUser?.uid === uid}
                showFollowButton={!!currentUser && currentUser.uid !== uid}
                onUserClick={onUserSelect}
                onFollowToggle={uid => followStats.followedUsers.includes(uid) ? unfollowUser(uid) : followUser(uid)}
                size="compact"
              />
            );
          })}
        </Box>
      )}

      {links.personIds && links.personIds.map(id => (
        <CrewPersonLinkRow key={id} personId={id} onSelect={onPersonSelect} />
      ))}
    </Box>
  );
};

export default CrewPostLinkSections;
